import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Empty, Table, Td } from "../../components/UI";
import { ShieldCheck, Archive, Clock, AlertTriangle, Play } from "lucide-react";
import { toast } from "sonner";

// V1 data-retention dashboard. Live counts per collection plus a
// dry-run purge that never actually deletes. V2 backlog (faded) is
// documented at the bottom of the panel per user directive.

export default function DataRetentionSection() {
  const [status, setStatus] = useState(null);
  const [dryOpen, setDryOpen] = useState(null);
  const [busyCol, setBusyCol] = useState(null);

  useEffect(() => {
    api.get("/data-retention/status").then((r) => setStatus(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const dryRun = async (col) => {
    setBusyCol(col);
    try {
      const r = await api.post("/data-retention/purge-dry-run", { collection: col });
      setDryOpen(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyCol(null); }
  };

  if (!status) return <div className="py-10 text-center text-sm text-[#84675B]">Loading retention status…</div>;

  const clinicalPurgeable = status.rows.filter((r) => r.eligible_for_purge > 0).length;

  return (
    <div className="space-y-5" data-testid="settings-data-retention">
      <div className="grid gap-3 md:grid-cols-4">
        <Card className="p-4" data-testid="dr-card-adults">
          <div className="flex items-center gap-2 text-[#7B6157]"><Clock size={13} /><span className="text-[10px] font-bold uppercase tracking-wide">Adult records</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#3E2723]">7 years</div>
          <div className="text-xs text-[#7B6157]">post last-service</div>
        </Card>
        <Card className="p-4" data-testid="dr-card-minors">
          <div className="flex items-center gap-2 text-[#7B6157]"><Clock size={13} /><span className="text-[10px] font-bold uppercase tracking-wide">Minors</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#3E2723]">7 yrs or age 21</div>
          <div className="text-xs text-[#7B6157]">whichever is later</div>
        </Card>
        <Card className="p-4" data-testid="dr-card-federal">
          <div className="flex items-center gap-2 text-[#7B6157]"><ShieldCheck size={13} /><span className="text-[10px] font-bold uppercase tracking-wide">Federal minimum</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#3E2723]">6 years</div>
          <div className="text-xs text-[#7B6157]">HIPAA § 164.316(b)(2)(i)</div>
        </Card>
        <Card className="p-4" data-testid="dr-card-eligible">
          <div className="flex items-center gap-2 text-[#7B6157]"><Archive size={13} /><span className="text-[10px] font-bold uppercase tracking-wide">Eligible for purge</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#8D5A00]">{clinicalPurgeable}</div>
          <div className="text-xs text-[#7B6157]">collections with rows past horizon</div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="border-b border-[#F1EEE7] px-5 py-3">
          <h3 className="font-heading text-base font-bold text-[#3E2723]">Live retention status</h3>
          <p className="mt-0.5 text-[11px] text-[#7B6157]">Per-collection totals as of {(status.generated_at || "").replace("T", " ").slice(0, 16)} UTC. Dry-run only. No records are ever deleted in V1.</p>
        </div>
        <Table headers={["Collection", "Total records", "Retention horizon", "Eligible for purge", "Action"]}>
          {status.rows.map((r) => (
            <tr key={r.collection} className="border-b border-[#EFEBE0] last:border-0" data-testid={`dr-row-${r.collection}`}>
              <Td>
                <div className="text-sm font-semibold text-[#3E2723]">{r.label}</div>
                <div className="text-[10px] text-[#84675B] font-mono">{r.collection}</div>
              </Td>
              <Td><span className="font-heading text-base font-bold text-[#3E2723]">{r.total}</span></Td>
              <Td>
                {r.retention_days < 0
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-[#E3F2FD] px-2 py-0.5 text-[10px] font-bold text-[#1565C0]"><ShieldCheck size={9} /> Indefinite</span>
                  : <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold text-[#4E342E]">{r.retention_label}</span>}
              </Td>
              <Td>
                {r.eligible_for_purge > 0
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[10px] font-bold text-[#8D5A00]"><AlertTriangle size={9} /> {r.eligible_for_purge}</span>
                  : <span className="text-[11px] text-[#7B6157]">-</span>}
              </Td>
              <Td>
                {r.retention_days < 0
                  ? <span className="text-[10px] text-[#84675B]">n/a</span>
                  : <Btn variant="outline" data-testid={`dr-dry-run-${r.collection}`} onClick={() => dryRun(r.collection)}
                      disabled={busyCol === r.collection} className="px-3 py-1 text-[11px]">
                      <Play size={11} /> {busyCol === r.collection ? "Running…" : "Dry-run purge"}
                    </Btn>}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {dryOpen && (
        <div className="fixed inset-0 z-[80] bg-[#3E2723]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDryOpen(null)}>
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-[#3E2723]">Dry-run: {dryOpen.label}</h3>
              <button data-testid="dr-modal-close" onClick={() => setDryOpen(null)} className="text-[#7B6157] hover:text-[#3E2723]">×</button>
            </div>
            <div className="mb-3 rounded-xl border border-[#FFCC80] bg-[#FFF3E0] p-3 text-xs text-[#6D4C41]">
              <div className="font-bold text-[#3E2723]">No records were deleted.</div>
              <div className="mt-1">{dryOpen.note}</div>
            </div>
            <div className="mb-3 flex items-center justify-between rounded-lg bg-[#F5F2EB] px-3 py-2 text-sm text-[#3E2723]">
              <div>Cutoff: <span className="font-mono text-xs">{dryOpen.cutoff || "-"}</span></div>
              <div>Total eligible: <span className="font-bold">{dryOpen.total_eligible}</span></div>
            </div>
            <div className="max-h-64 overflow-y-auto text-xs">
              <div className="mb-1 text-[10px] uppercase font-bold text-[#7B6157] tracking-wider">Sample rows (first 25)</div>
              {(dryOpen.samples || []).length === 0 ? <Empty text="No eligible rows" /> : (
                <table className="w-full text-xs">
                  <thead className="text-left text-[10px] uppercase text-[#7B6157]"><tr><th className="py-1">Row ID</th><th className="py-1">Created</th></tr></thead>
                  <tbody>{dryOpen.samples.map((s, i) => (
                    <tr key={i} className="border-b border-[#F1EEE7]"><td className="py-1 font-mono">{(s.id || "-").slice(0, 12)}</td><td className="py-1 font-mono">{(s.created_at || "-").slice(0, 19).replace("T", " ")}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* V2 backlog: faded per user directive to signal upcoming */}
      <Card className="p-5 opacity-60 relative" data-testid="dr-v2-backlog">
        <span className="absolute top-4 right-5 rounded-full bg-[#FFB300] px-2.5 py-0.5 text-[10px] font-bold text-[#3E2723]">Version 2</span>
        <h3 className="mb-2 font-heading text-base font-bold text-[#3E2723]">Coming in Version 2</h3>
        <ul className="space-y-1.5 text-xs text-[#6D4C41] list-disc pl-5">
          <li>Scheduled nightly purge job with per-tenant policy override</li>
          <li>Restore-from-archive workflow with dual admin approval</li>
          <li>Legal-hold flag that suspends purge on select clients or resources</li>
          <li>Per-state overrides (California adults 7 yrs, minors until 25; Texas adults 7 yrs, minors until 21; Florida 5 yrs)</li>
          <li>Retention reports emailed monthly to the Privacy Officer</li>
          <li>Purge audit report with cryptographic proof of deletion</li>
        </ul>
      </Card>
    </div>
  );
}
